import { useTraderTime } from '../contexts/TraderTimeContext';
import { ThemeToggle } from '../components/ThemeToggle';
import React, { useState } from 'react';
import { useSearchParams, useNavigate, Navigate } from 'react-router-dom';
import { Sidebar } from '../components/Sidebar';
import { Button } from '../components/ui/button';
import { ArrowLeftToLine, LogOut, Menu, X } from 'lucide-react';
import { clearTraderImpersonation, isTraderImpersonationActive, traderAuthAPI } from '../lib/api';
import { cabinetUrl } from '../lib/domains';

// Импортируем существующие компоненты
import { DepositRequisitesPage } from './DepositRequisitesPage';
import { WithdrawRequisitesPage } from './WithdrawRequisitesPage';
import { DepositArchivePage } from './DepositArchivePage';
import { WithdrawArchivePage } from './WithdrawArchivePage';
import { ActivePaymentsPage } from './ActivePaymentsPage';
import { HistoryPage } from './HistoryPage';
import { AllRequisitesPage } from './AllRequisitesPage';
import { DepositBalance } from '../components/trader/DepositBalance';
import { TraderAutomation } from '../components/trader/TraderAutomation';
import { TraderNotifications } from '../components/trader/TraderNotifications';
import { TraderSettings } from '../components/trader/TraderSettings';
import { BankStatements } from '../components/BankStatements';

export const TraderDashboardPage: React.FC = () => {
    const { label } = useTraderTime();
    const [searchParams] = useSearchParams();
    const [menuOpen, setMenuOpen] = useState(false);
    const navigate = useNavigate();
    const activeTab = searchParams.get('tab') || 'orders';
    const isImpersonation = isTraderImpersonationActive();

    const handleLogout = () => {
        if (isImpersonation) {
            clearTraderImpersonation();
            window.location.assign(cabinetUrl('admin', '/dashboard'));
            return;
        }

        traderAuthAPI.logout();
        navigate('/');
    };

    const renderContent = () => {
        switch (activeTab) {
            case 'bank-statements':
                return <BankStatements audience="trader" />;
            case 'all-requisites':
                return <AllRequisitesPage />;
            case 'deposit-working':
                return <DepositRequisitesPage />;
            case 'deposit-archive':
                return <DepositArchivePage />;
            case 'withdraw-working':
                return <WithdrawRequisitesPage />;
            case 'withdraw-archive':
                return <WithdrawArchivePage />;
            case 'active': {
                const params = new URLSearchParams(searchParams);
                params.set('tab', 'active-deposit');
                return <Navigate to={{ search: `?${params}` }} replace />;
            }
            case 'active-deposit':
                return <ActivePaymentsPage direction="deposit" />;
            case 'active-withdraw':
                return <ActivePaymentsPage direction="withdraw" />;
            case 'deposits':
                return <DepositBalance />;
            case 'history':
                return <HistoryPage />;
            case 'automation':
                return <TraderAutomation />;
            case 'notifications':
                return <TraderNotifications />;
            case 'settings':
                return <TraderSettings />;
            default:
                return <AllRequisitesPage />;
        }
    };

    return (
        <div className="flex min-h-screen bg-gray-50 dark:bg-slate-900">
            {menuOpen && (
                <button className="fixed inset-0 z-30 bg-black/40 xl:hidden" aria-label="Закрыть меню" onClick={() => setMenuOpen(false)} />
            )}
            <div id="trader-navigation" className={`${menuOpen ? 'block' : 'hidden'} fixed inset-y-0 left-0 z-40 overflow-y-auto xl:static xl:block xl:shrink-0`} onClick={event => {
                if ((event.target as HTMLElement).closest('a')) setMenuOpen(false);
            }}>
                <Sidebar />
            </div>
            <div className="min-w-0 flex-1 flex flex-col">
                {/* Header с кнопкой выхода */}
                <div className="bg-white dark:bg-slate-800 border-b border-gray-200 dark:border-slate-700 px-4 sm:px-8 py-4">
                    <div className="flex items-center justify-between xl:justify-end">
                        <Button variant="outline" className="xl:hidden" aria-label={menuOpen ? 'Закрыть меню' : 'Открыть меню'} aria-expanded={menuOpen} aria-controls="trader-navigation" onClick={() => setMenuOpen(open => !open)}>
                            {menuOpen ? <X className="h-4 w-4" /> : <Menu className="h-4 w-4" />}
                        </Button>
                        <div className="flex items-center gap-2">
                            <span className="text-sm text-gray-500 dark:text-slate-400">{label}</span>
                            <Button
                                onClick={handleLogout}
                                variant="outline"
                                className="flex items-center gap-2 text-gray-600 dark:text-slate-300 hover:text-red-600 dark:hover:text-red-300 hover:border-red-300 dark:hover:border-red-800"
                            >
                                {isImpersonation ? (
                                    <ArrowLeftToLine className="h-4 w-4" />
                                ) : (
                                    <LogOut className="h-4 w-4" />
                                )}
                                {isImpersonation ? 'В админку' : 'Выйти'}
                            </Button>
                            <ThemeToggle />
                        </div>
                    </div>
                </div>

                {/* Основной контент */}
                <div className="min-w-0 flex-1 p-4 sm:p-8">
                    {renderContent()}
                </div>
            </div>
        </div>
    );
};
